import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSocket } from '../../../contexts/SocketContext';
import DiceGame from './DiceGame';
import HighLow from './HighLow';
import ChuckALuck from './ChuckALuck';
import styles from './GamblingHall.module.css';

// ── Shared constants ─────────────────────────────────────────────────────────
export const GAMBLING_DAILY_LIMIT = 1000;
const FEED_POLL_MS = 5 * 60 * 1000;

// ── Shared types ─────────────────────────────────────────────────────────────
export interface RecentRoll {
  name: string;
  game?: string;
  roll1?: number;
  roll2?: number;
  roll3?: number;
  sum?: number;
  bet: number;
  outcome: 'win' | 'loss';
  amount: number;
  ts: number;
}

/** Props passed down to every game component. */
export interface GamblingGameProps {
  mercenaryId: number;
  silver: number;
  remaining: number;
  nextRollAt: Date | null;
  onResult: (newSilver: number, remaining: number, nextRollAt: Date | null) => void;
}

// ── Feed sub-components ──────────────────────────────────────────────────────
function rollLabel(r: RecentRoll): string {
  if (r.roll3 != null) return `${r.roll1}·${r.roll2}·${r.roll3}`;
  if (r.roll1 != null && r.roll2 != null) return `${r.roll1}+${r.roll2}`;
  return '';
}

function gameLabel(game?: string): string {
  if (game === 'highlow')    return 'Fortuna';
  if (game === 'chuckaluck') return 'Fates';
  return 'Lucky 7';
}

const FeedRow: React.FC<{ entry: RecentRoll; type: 'win' | 'loss' }> = ({ entry, type }) => (
  <div className={type === 'win' ? styles.feedRowWin : styles.feedRowLoss}>
    <span className={styles.feedName}>{entry.name}</span>
    <span className={styles.feedGame}>{gameLabel(entry.game)}</span>
    <span className={styles.feedRoll}>{rollLabel(entry)}</span>
    <span className={styles.feedAmount}>{type === 'win' ? `+${entry.amount}` : `-${entry.amount}`}</span>
  </div>
);

const FeedBox: React.FC<{ title: string; entries: RecentRoll[]; type: 'win' | 'loss' }> = ({ title, entries, type }) => (
  <div className={styles.feedBox}>
    <div className={`${styles.feedTitle} ${type === 'win' ? styles.feedTitleWin : styles.feedTitleLoss}`}>
      {title}
    </div>
    <div className={styles.feedList}>
      {entries.length === 0 && <span className={styles.feedEmpty}>No rolls yet</span>}
      {entries.map((e, i) => <FeedRow key={i} entry={e} type={type} />)}
    </div>
  </div>
);

// ── Main component ───────────────────────────────────────────────────────────
type GameId = 'lucky7' | 'highlow' | 'chuckaluck';

interface GamblingHallProps {
  mercenaryId: number;
  initialSilver: number;
}

const GamblingHall: React.FC<GamblingHallProps> = ({ mercenaryId, initialSilver }) => {
  const [activeGame, setActiveGame] = useState<GameId>('lucky7');
  const [silver, setSilver] = useState(initialSilver);
  const [remaining, setRemaining] = useState(GAMBLING_DAILY_LIMIT);
  const [nextRollAt, setNextRollAt] = useState<Date | null>(null);
  const [recentRolls, setRecentRolls] = useState<RecentRoll[]>([]);
  const { socket } = useSocket();
  const pendingTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Fetch initial gambling status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`/api/tavern/gambling-status/${mercenaryId}`);
      setRemaining(res.data.remaining);
      setNextRollAt(res.data.nextRollAt ? new Date(res.data.nextRollAt) : null);
    } catch { /* silent */ }
  }, [mercenaryId]);

  // Fetch recent rolls (polling fallback)
  const fetchRecentRolls = useCallback(async () => {
    try {
      const res = await axios.get('/api/tavern/recent-rolls');
      setRecentRolls(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    fetchRecentRolls();
    const id = setInterval(fetchRecentRolls, FEED_POLL_MS);
    return () => clearInterval(id);
  }, [fetchRecentRolls]);

  // Real-time feed via socket - 5 s delay so dice result shows first
  useEffect(() => {
    if (!socket) return;
    const onRoll = (roll: RecentRoll) => {
      const id = setTimeout(() => {
        setRecentRolls(prev => [roll, ...prev].slice(0, 40));
      }, 5000);
      pendingTimers.current.push(id);
    };
    socket.on('tavernRoll', onRoll);
    return () => {
      socket.off('tavernRoll', onRoll);
      pendingTimers.current.forEach(clearTimeout);
      pendingTimers.current = [];
    };
  }, [socket]);

  // Callback given to each game component
  const handleResult = useCallback((newSilver: number, newRemaining: number, newNextRollAt: Date | null) => {
    setSilver(newSilver);
    setRemaining(newRemaining);
    if (newNextRollAt) setNextRollAt(newNextRollAt);
  }, []);

  const gameProps: GamblingGameProps = { mercenaryId, silver, remaining, nextRollAt, onResult: handleResult };
  const wins   = recentRolls.filter(r => r.outcome === 'win');
  const losses = recentRolls.filter(r => r.outcome === 'loss');

  return (
    <div className={styles.hall}>
      {/* Game selector tabs */}
      <div className={styles.gameTabs}>
        <button
          className={`${styles.gameTab} ${activeGame === 'lucky7' ? styles.gameTabActive : ''}`}
          onClick={() => setActiveGame('lucky7')}
        >
          Lucky 7
        </button>
        <button
          className={`${styles.gameTab} ${activeGame === 'highlow' ? styles.gameTabActive : ''}`}
          onClick={() => setActiveGame('highlow')}
        >
          Fortuna's Call
        </button>
        <button
          className={`${styles.gameTab} ${activeGame === 'chuckaluck' ? styles.gameTabActive : ''}`}
          onClick={() => setActiveGame('chuckaluck')}
        >
          Three Fates
        </button>
      </div>

      {/* Active game */}
      <div className={styles.gameArea}>
        {activeGame === 'lucky7'     && <DiceGame    {...gameProps} />}
        {activeGame === 'highlow'    && <HighLow     {...gameProps} />}
        {activeGame === 'chuckaluck' && <ChuckALuck  {...gameProps} />}
      </div>

      {/* Daily limit info */}
      <div className={styles.hallStatus}>
        Daily limit: <strong>{remaining} / {GAMBLING_DAILY_LIMIT}</strong> silver remaining &nbsp;&middot;&nbsp; Your silver: <strong>{silver}</strong>
      </div>

      {/* Recent rolls feed */}
      <div className={styles.feedRow}>
        <FeedBox title="Recent Wins"   entries={wins}   type="win"  />
        <FeedBox title="Recent Losses" entries={losses} type="loss" />
      </div>
    </div>
  );
};

export default GamblingHall;
